<script lang="ts" setup>
    import { computed, onMounted, reactive, ref, watch } from 'vue';
    import '../../../../css/choice.css';
    import * as choiceApi from '../../../lib/ChoiceApi';
    import TabContainer from '../../../components/Section/Tabs/TabContainer.vue';
    import TabItem from '../../../components/Section/Tabs/TabItem.vue';
    import FoldingPanel from '../../../components/FoldingPanel.vue';
    import Icon from '../../../components/Icon.vue';
    import { IconTypes, ModalSize, ButtonDisplayStyle, ButtonDisplaySize, DateDisplayFormat } from '../../../Enums/UiEnums';
    import { EMRSummaryTimelineParameterOrder, EMRSummaryTimelineSettings, IEMRDateRangeOption, IEMRSummaryDataItem, IFishboneHeader, IFishboneType, ITimelineCustomResult, ITimelineCustomResultHeader, ITimelineFishboneResult, ITimelineResultAbnormal, ITimelineNewResultPayload, ITimelineResultPayloadItem, ITimelineResultType, ITimelineVital, ResultAbnormalOptions, TimelineResultAbnormal, TimelineResultTemplateControlTypes } from '../../../lib/Models/EMR/TimelineModel';
    import ModalDialog from '../../../components/ModalDialog.vue';
    import { ActionButtonStyle } from '../../../lib/Types/Types';
    import TextInput from '../../../components/TextInput.vue';
    import ActionButtonV2 from '../../../components/ActionButtonV2.vue';
    import { ModalStyles, popAlert } from '../../../lib/Notification';
    import { MeasurementSystem } from '../../../lib/Models/EMR/TimelineModel';
    import { DatabaseIdTypes, UserSettingGroup } from '../../../Enums/ChoiceEnums';
    import { IPatientServicePatientHeaderResponse } from '../../../lib/Models/PatientModel';
    import ChoiceButtonGroup from '../../../components/ChoiceButtonGroup.vue';
    import { IChoiceButtonGroupItem } from '../../../lib/Models/ChoiceButtonGroup';
    import DatePickerNoTZ from '../../../components/DatePickerNoTZ.vue';
    import vSelect from 'vue-select';
    import { ParameterValueTypes } from '../../../lib/Api/CompanySettings';

    const props = defineProps<{
        patientId: number
    }>();

    enum TabType {
        graph,
        table
    }

    const pageData = reactive<{
        selectedTab: TabType,
        patientId: number,
        patientInfo?: IPatientServicePatientHeaderResponse,
        resultsPanelOpen: boolean,
        vitalsPanelOpen: boolean,
        dateRange?: string,
        fishboneTypes: IFishboneType[],
        fishboneHeaders: IFishboneHeader[],
        fishboneResults: ITimelineFishboneResult[],
        customResultHeaders: ITimelineCustomResultHeader[],
        customResults: ITimelineCustomResult[],
        vitals: ITimelineVital[],
        data?: IEMRSummaryDataItem[]
    }>({
        selectedTab: TabType.table,
        patientId: -1,
        patientInfo: undefined,
        resultsPanelOpen: true,
        vitalsPanelOpen: true,
        fishboneTypes: [],
        fishboneHeaders: [],
        fishboneResults: [],
        customResultHeaders: [],
        customResults: [],
        vitals: [],
        data: undefined
    });

    const companySettings = reactive<{
        bpSystolicLow?: number,
        bpSystolicHigh?: number,
        bpDiastolicLow?: number,
        bpDiastolicHigh?: number,
        bmiLow?: number,
        bmiHigh?: number
    }>({});
    
    const editState = reactive<{
        vital?: ITimelineVital,
        fishboneResult?: ITimelineFishboneResult,
        customResult?: ITimelineCustomResult,
        newResult?: ITimelineNewResultPayload
    }>({
        vital: undefined,
        fishboneResult: undefined,
        customResult: undefined,
        newResult: undefined
    });

    const graphLaunchUrl = ref("");
    const dateRangeOptions: IChoiceButtonGroupItem[] = [
        { label: "3m", value: IEMRDateRangeOption.Three, click: async () => { setDateFilterSetting(IEMRDateRangeOption.Three); } },
        { label: "6m", value: IEMRDateRangeOption.Six, click: async () => { setDateFilterSetting(IEMRDateRangeOption.Six); } },
        { label: "1y", value: IEMRDateRangeOption.Year, click: async () => { setDateFilterSetting(IEMRDateRangeOption.Year); } },
        { label: "2y", value: IEMRDateRangeOption.TwoYear, click: async () => { setDateFilterSetting(IEMRDateRangeOption.TwoYear); } },
        { label: "All", value: IEMRDateRangeOption.All, click: async () => { setDateFilterSetting(IEMRDateRangeOption.All); } }
    ];
    const abnormalLowTextClass = "text-primary fw-bold";
    const abnormalHighTextClass = "text-danger fw-bold";

    onMounted(async () => {
        if (!props.patientId) {
            popAlert("Patient identifier was not provided.", "EMR Timeline", ModalStyles.info);
            return;
        }
        graphLaunchUrl.value = `/officemd/screens/EMRSummary_VitalsGraph.htm?id=${props.patientId}`;

        const [
            tableView,
            patientInfo,
            resultPanelOpen,
            vitalsPanelOpen,
            dateRange,
            bpSystolicLow,
            bpSystolicHigh,
            bpDiastolicLow,
            bpDiastolicHigh,
            bmiLow,
            bmiHigh
        ] = await Promise.all([
            choiceApi.user.getSetting(UserSettingGroup.TimelineSettings, EMRSummaryTimelineSettings.Table),
            choiceApi.patient.getSimpleHeader(DatabaseIdTypes.patient_id, props.patientId),
            choiceApi.user.getSetting(UserSettingGroup.EMRTimelineSettings,EMRSummaryTimelineSettings.ResultExpanded),
            choiceApi.user.getSetting(UserSettingGroup.EMRTimelineSettings,EMRSummaryTimelineSettings.VitalsExpanded),
            choiceApi.user.getSetting(UserSettingGroup.EMRTimelineSettings, EMRSummaryTimelineSettings.LookbackRange),
            choiceApi.companySettings.getCompanySetting(
                { 
                    parameterName: EMRSummaryTimelineSettings.VitalsParameter,
                    parameterOrder: EMRSummaryTimelineParameterOrder.BpSystolicLow,
                    valueType: ParameterValueTypes.Number
                }
            ),
            choiceApi.companySettings.getCompanySetting(
                { 
                    parameterName: EMRSummaryTimelineSettings.VitalsParameter,
                    parameterOrder: EMRSummaryTimelineParameterOrder.BpSystolicHigh,
                    valueType: ParameterValueTypes.Number
                }
            ),
            choiceApi.companySettings.getCompanySetting(
                { 
                    parameterName: EMRSummaryTimelineSettings.VitalsParameter,
                    parameterOrder: EMRSummaryTimelineParameterOrder.BpDiastolicLow,
                    valueType: ParameterValueTypes.Number
                }
            ),
            choiceApi.companySettings.getCompanySetting(
                { 
                    parameterName: EMRSummaryTimelineSettings.VitalsParameter,
                    parameterOrder: EMRSummaryTimelineParameterOrder.BpDiastolicHigh,
                    valueType: ParameterValueTypes.Number
                }
            ),
            choiceApi.companySettings.getCompanySetting(
                { 
                    parameterName: EMRSummaryTimelineSettings.VitalsParameter,
                    parameterOrder: EMRSummaryTimelineParameterOrder.BmiLow,
                    valueType: ParameterValueTypes.Number
                }
            ),
            choiceApi.companySettings.getCompanySetting(
                { 
                    parameterName: EMRSummaryTimelineSettings.VitalsParameter,
                    parameterOrder: EMRSummaryTimelineParameterOrder.BmiHigh,
                    valueType: ParameterValueTypes.Number
                }
            )
        ]);

        pageData.selectedTab = tableView == "N" ? TabType.graph : TabType.table;
        if (!patientInfo) {
            popAlert("Failed to get patient info", "EMR Timeline", ModalStyles.info);
            return;
        }
        pageData.patientInfo = patientInfo;
        pageData.resultsPanelOpen = resultPanelOpen === 'N' ? false : true;
        pageData.vitalsPanelOpen = vitalsPanelOpen === 'N' ? false : true;
        pageData.dateRange = dateRange ?? IEMRDateRangeOption.All;

        companySettings.bpSystolicLow = Number(bpSystolicLow?.parameterValue) ?? undefined;
        companySettings.bpSystolicHigh = Number(bpSystolicHigh?.parameterValue) ?? undefined;
        companySettings.bpDiastolicLow = Number(bpDiastolicLow?.parameterValue) ?? undefined;
        companySettings.bpDiastolicHigh = Number(bpDiastolicHigh?.parameterValue) ?? undefined;
        companySettings.bmiLow = Number(bmiLow?.parameterValue) ?? undefined;
        companySettings.bmiHigh = Number(bmiHigh?.parameterValue) ?? undefined;

        await initialize();
    });

    async function setDateFilterSetting(option: IEMRDateRangeOption) {
        const result = await choiceApi.user.saveSetting(UserSettingGroup.EMRTimelineSettings, EMRSummaryTimelineSettings.LookbackRange, option);
        if (result) {
            pageData.dateRange = option;
            initialize();
        } else {
            popAlert("Failed to update user setting.", "EMR Timeline Lookback", ModalStyles.info);
            return;
        }
    }

    const filteredData = computed(() => { 
        if (pageData.resultsPanelOpen && pageData.vitalsPanelOpen) {
            return pageData.data;
        }
        else if (pageData.resultsPanelOpen) {
            return pageData.data?.filter(item => item.fbResults?.length || item.customResults?.length);
        }
        else if (pageData.vitalsPanelOpen) {
            return pageData.data?.filter(item => item.vitals?.length);
        }
        return pageData.data;
    });

    const resultTypes = computed(() => {
        let types: ITimelineResultType[] = [];
        pageData.fishboneHeaders.forEach(header => {
            types.push({ isCustomResult: false, id: header.fbId, displayName: header.fbDescription });
        });
        return types;
    })

    async function initialize() {
        const [vitals, fishboneTypes, fishboneResults, customResults] = await Promise.all([
            choiceApi.timeline.emrSummaryVitals(props.patientId),
            choiceApi.timeline.fishboneTypes(),
            choiceApi.timeline.fishboneResults(props.patientId),
            choiceApi.timeline.customResults(props.patientId)
        ]);

        pageData.vitals = vitals;
        pageData.fishboneTypes = fishboneTypes;
        pageData.fishboneTypes.forEach(fbType => {
            if (pageData.fishboneHeaders.find(header => header.fbDescription == fbType.fbDescription) === undefined) {
                pageData.fishboneHeaders.push({ fbId: fbType.fbId, fbDescription: fbType.fbDescription });
            } 
        });
        pageData.fishboneResults = fishboneResults;
        pageData.customResults = customResults;
        pageData.customResults.forEach(customResult => {
            if (pageData.customResultHeaders.find(header => header.globalEntityId == customResult.globalEntityId) === undefined) {
                pageData.customResultHeaders.push({ globalEntityId: customResult.globalEntityId, description: customResult.description });
            }
        });

        buildData();
    }

    function buildData() {
        pageData.data = [];
        // create a data item for each unique date from fishbone and vitals data
        pageData.fishboneResults.forEach(result => {
            if (pageData.data && !pageData.data.find(item => item.date == result.labReportDate)) {
                pageData.data.push({ date: result.labReportDate });
            }
        })
        pageData.customResults.forEach(result => {
            if (pageData.data && !pageData.data.find(item => item.date == result.encounterDate)) {
                pageData.data.push({ date: result.encounterDate });
            }
        })
        pageData.vitals.forEach(entry => {
            if (pageData.data && !pageData.data.find(item => item.date == entry.encounterDate)) {
                pageData.data.push({ date: entry.encounterDate });
            }
        })

        // sort data items by date
        pageData.data.sort(function(a, b) { return new Date(b.date).getTime() - new Date(a.date).getTime() });

        // populate data items with fishbone results  and vitals from each day
        pageData.data.forEach(item => {
            let results: ITimelineFishboneResult[] = [];
            pageData.fishboneResults.forEach(result => {
                if (item.date == result.labReportDate) {
                    results.push(result);
                }
            });
            item.fbResults = results;

            let customResults: ITimelineCustomResult[] = [];
            pageData.customResults.forEach(result => {
                if (item.date == result.encounterDate) {
                    customResults.push(result);
                }
            });
            item.customResults = customResults;

            let vitals: ITimelineVital[] = [];
            pageData.vitals.forEach(entry => {
                if (item.date == entry.encounterDate) {
                    vitals.push(entry);
                }
            });
            item.vitals = vitals;
        })
    }

    async function updatePanelExpandedSetting(isResultsPanel: boolean) {
        const codeName = isResultsPanel ? EMRSummaryTimelineSettings.ResultExpanded : EMRSummaryTimelineSettings.VitalsExpanded;
        const value = isResultsPanel ? pageData.resultsPanelOpen : pageData.vitalsPanelOpen;
        const success = await choiceApi.user.saveSetting(UserSettingGroup.EMRTimelineSettings, codeName, value ? "Y" : "N");
        if (!success) {
            popAlert("Failed to update panel expanded state", "User Setting Update", ModalStyles.warning);
        } 
    }

    function fishboneResultExists(fbId: number): boolean {
        return pageData.fishboneResults.some(fbResult => fbResult.fbId == fbId);
    }

    function fishboneResultItemExists(fbSubId: number): boolean {
        return pageData.fishboneResults.some(fbResult => fbResult.fbSubId == fbSubId);
    }

    function getFishboneResult(fbSubId: number, fbResults?: ITimelineFishboneResult[]): ITimelineFishboneResult | undefined {
        return fbResults?.filter(result => result.fbSubId == fbSubId)?.[0];
    }

    function getFishboneResultTitle(fbSubId: number, fbResults?: ITimelineFishboneResult[]): string {
        let result = "";
        if (!fbResults || !fbResults.length) {
            return result;
        }
        fbResults = fbResults.filter(fbResult => fbResult.fbSubId === fbSubId);
        if (fbResults.length <= 1) {
            return result;
        }
        result = `+${fbResults.length-1} more:\n`;
        fbResults.forEach((fbResult, i) => { if ( i !== 0 && fbResult.fbSubId === fbSubId) result += `${fbResult.resultValue} ${fbResult.unitOfMeasure}\n` });
        return result;
    }

    function getFishboneResultValue(fbSubId: number, fbResults?: ITimelineFishboneResult[]): string | undefined {
        const result = fbResults?.filter(result => result.fbSubId == fbSubId)?.[0];
        return result ? `${result.resultValue?.substring(0,15)} ${result.unitOfMeasure}` : undefined;
    }

    function getFishboneResultValueTextColor(fbSubId: number, fbResults?: ITimelineFishboneResult[]): string {
        let color = "";
        const result = getFishboneResult(fbSubId, fbResults);
        const abnormal = result?.resultAbnormalFlags;
        if (abnormal == TimelineResultAbnormal.Below 
            || abnormal == TimelineResultAbnormal.Low
            || abnormal == TimelineResultAbnormal.ReallyLow) 
        {
            color = abnormalLowTextClass;
        }
        else if (abnormal == TimelineResultAbnormal.Above 
            || abnormal == TimelineResultAbnormal.High
            || abnormal == TimelineResultAbnormal.ReallyHigh) 
        {
            color = abnormalHighTextClass;
        }
        return color;
    }

    function getBmiTextColor(bmiRaw?: string): string {
        let color = "";
        if (isNaN(Number(bmiRaw))) {
            return color;
        }
        let bmi = Number(bmiRaw);
        if (bmi && companySettings.bmiLow) {
            color = bmi <= companySettings.bmiLow ?  abnormalLowTextClass : color;
        }
        if (bmi && companySettings.bmiHigh) {
            color = bmi >= companySettings.bmiHigh ?  abnormalHighTextClass : color;
        }
        return color;
    }

    function getSystolicTextColor(systolic?: number): string {
        let color = "";
        if (!systolic) {
            return color;
        }
        if (companySettings.bpSystolicLow) {
            color = systolic <= companySettings.bpSystolicLow ?  abnormalLowTextClass : color;
        }
        if (companySettings.bpSystolicHigh) {
            color = systolic >= companySettings.bpSystolicHigh ?  abnormalHighTextClass : color;
        }
        return color;
    }

    function getDiastolicTextColor(diastolic?: number): string {
        let color = "";
        if (!diastolic) {
            return color;
        }
        if (companySettings.bpDiastolicLow) {
            color = diastolic <= companySettings.bpDiastolicLow ?  abnormalLowTextClass : color;
        }
        if (companySettings.bpDiastolicHigh) {
            color = diastolic >= companySettings.bpDiastolicHigh ?  abnormalHighTextClass : color;
        }
        return color;
    }

    async function openOrder(orderDate: string) {
        const screenWidth = window.screen.width;
        const screenHeight = window.screen.height;
        const left = (screenWidth - 800) / 2;
        const top = (screenHeight - 400) / 2;
        const window_features = `popup=yes,resizable=yes,width=860,height=680,top=${top},left=${left}`;
        window.open(`/officemd/screens/EMRSummary_Results.htm?id=${props.patientId}&date=${orderDate}`,"Order Results",window_features);
    }

    async function updateFishoneResult() {
        const fishboneResult = editState.fishboneResult;
        if (fishboneResult) {
            const result = await choiceApi.timeline.updateFishboneResult({
                patientId: props.patientId,
                isCustomResult: false,
                resultIdentifier1: fishboneResult.resultIdentifier1,
                resultIdentifier2: fishboneResult.resultIdentifier2,
                resultLabel: fishboneResult.fbResultLabel,
                resultValue: fishboneResult.resultValue
            });
            if (result) { // error
                popAlert(result, "Edit Fishbone Result", ModalStyles.warning);
            }
            editState.fishboneResult = undefined;
            initialize();
        }
    }

    async function updateCustomResult() {
        const customResult = editState.customResult;
        if (customResult) {
            const result = await choiceApi.timeline.updateCustomResult({
                patientId: props.patientId,
                isCustomResult: true,
                resultIdentifier1: customResult.globalEntityId,
                resultIdentifier2: customResult.encounterId,
                resultLabel: customResult.description,
                resultValue: customResult.encounterValue
            });
            if (result) { // error
                popAlert(result, "Edit Custom Result", ModalStyles.warning);
            }
            editState.customResult = undefined;
            initialize();
        }
    }

    function allResultsHaveTemplate(results: ITimelineResultPayloadItem[]): boolean {
        return !results.some(result => result.value && !result.template);
    }

    function allResultsHaveCorrectType(results: ITimelineResultPayloadItem[]): string | undefined {
        let error = undefined;
        results.forEach(result => {
            if (result.template && result.value !== undefined) {
                if (result.template.controlTypeId == TimelineResultTemplateControlTypes.Checkbox && !["Y","N", ""].includes(result.value)) {
                    error = `${result.fbSubLabel} must be blank, Y or N.`;
                }
                if (result.template.controlTypeId === TimelineResultTemplateControlTypes.Numeric && isNaN(Number(result.value))) {
                    error = `${result.fbSubLabel} must be a numeric value.`;
                }
                if (result.template.controlTypeId === TimelineResultTemplateControlTypes.Date) {
                    const date = new Date(result.value);
                    if (!isNaN(date.getTime())) {
                        error = `${result.fbSubLabel} must be a date value (mm/dd/yyy).`;
                    }
                }
            }
        })
        return error;
    }

    function isValidVitalData(vital: ITimelineVital) {
        return (vital.temperature == null || !isNaN(vital.temperature))
               && (vital.heightPrimaryValue == null || !isNaN(vital.heightPrimaryValue))
               && (vital.heightSecondaryValue == null || !isNaN(vital.heightSecondaryValue))
               && (vital.weightPrimaryValue == null || !isNaN(vital.weightPrimaryValue))
               && (vital.weightSecondaryValue == null || !isNaN(vital.weightSecondaryValue))
               && (vital.pain == null || !isNaN(vital.pain))
               && (vital.pulse == null || !isNaN(vital.pulse))
               && (vital.systolic == null || !isNaN(vital.systolic))
               && (vital.diastolic == null || !isNaN(vital.diastolic))
               && (vital.spO2 == null || !isNaN(vital.spO2))
        ;
    }

    function isValidPain(vital: ITimelineVital) {
        return vital.pain == null || (vital.pain >= 1 && vital.pain <= 10);
    }

    async function updateVital() {
        const vital = editState.vital;
        let error = false;
        if (vital) {
            if (!isValidVitalData(vital)) {
                error = true;
                popAlert("Expected numeric values", "Edit Vitals", ModalStyles.warning);
            }
            if (!error && !isValidPain(vital)) {
                error = true;
                popAlert("Expected valued between 1 and 10", "Edit Vitals", ModalStyles.warning);
            }
            if (!error) {
                const result = await choiceApi.timeline.updateVital({
                    vitalsId: vital.vitalsId,
                    patientId: props.patientId,
                    temperature: vital.temperature,
                    heightPrimaryValue: vital.heightPrimaryValue,
                    heightSecondaryValue: vital.heightSecondaryValue,
                    weightPrimaryValue: vital.weightPrimaryValue,
                    weightSecondaryValue: vital.weightSecondaryValue,
                    respirations: vital.respirations,
                    pain: vital.pain,
                    pulse: vital.pulse,
                    systolic: vital.systolic,
                    diastolic: vital.diastolic,
                    pulseOximetryId: vital.pulseOximetryId,
                    spO2: vital.spO2
                });
                if (result) { // error
                    popAlert(result, "Edit Vitals", ModalStyles.warning);
                }
            }
            editState.vital = undefined;
            initialize();
        }
    }

    function initializeNewResult(): void {
        editState.newResult = { 
            patientId: props.patientId,
            date: new Date().toLocaleDateString(),
            resultItems: []
        };
    }

    function initializeNewResultWithResultType(resultType: number): void {
        editState.newResult = { 
            patientId: props.patientId,
            date: new Date().toLocaleDateString(),
            resultItems: [],
            resultType: resultType
        };
    }

    function resultTypeSelect(): void {
        if (editState.newResult) {
            editState.newResult.resultItems = [];
        }
        pageData.fishboneTypes.filter(fbType => fbType.fbId == editState.newResult?.resultType && fbType.templates?.length).forEach(fbType => {
            editState.newResult?.resultItems.push({ fbSubId: fbType.fbSubId, fbSubLabel: fbType.fbSubLabel });
        });
    }

    watch(() => editState.newResult?.resultType, () => {
        resultTypeSelect();
    });
    watch(
        () => editState.newResult?.resultItems, () => {
            editState.newResult?.resultItems.forEach(resultItem => {
                let fishboneType = pageData.fishboneTypes.find(item => item.fbSubId == resultItem.fbSubId); 
                if (fishboneType?.templates?.length && !resultItem.template) {
                    resultItem.template = fishboneType.templates[0];
                }
            })
        },
        { deep: true, immediate: true }
    );

    async function createNewResult() {
        const newResult = editState.newResult;
        if (newResult && newResult.resultItems) {
            // remove any payload items that don't have a result
            newResult.resultItems = newResult.resultItems.filter(result => result.value);

            // validate each result with a value has a template selected
            if (!allResultsHaveTemplate(newResult.resultItems)) {
                editState.newResult = undefined;
                popAlert("Result value cannot be saved without a template", "New Result", ModalStyles.warning);
                return;
            }
            // validate each result has correct type
            let error = allResultsHaveCorrectType(newResult.resultItems);
            if (error) {
                editState.newResult = undefined;
                popAlert(error, "New Result", ModalStyles.warning);
                return;
            }
            error = await choiceApi.timeline.createNewFishboneResult(newResult);
            if (error) {
                editState.newResult = undefined;
                popAlert(error, "New Result", ModalStyles.warning);
                return;
            }
        }
        editState.newResult = undefined;
        initialize();
    }
</script>

<style scoped>
    #summaryVitals {
        width: 99vw;
        height: 99vh;
        overflow: hidden;
    }
    #summaryVitals * {
        font-size: small;
    }
    #summaryVitals .modalHeader {
        font-size: medium;
        font-weight: bold;
    }
    .tab {
        min-width: 400%;
        max-height: 87.25vh;
        overflow: scroll;
        position: relative;
    }
    #header {
        width: 99.5%;
        height: 2.5rem;
    }
    .pointer, .click {
        cursor: pointer;
    }
    .pointer:hover {
        background-color: #F3F8FF;
    }
    .dateLabelColumn,
    .dataLabelColumn {
        width: 200px;
        z-index: 10;
    }
    .sticky {
        position: sticky;
        left: 0;
    }
    .dateCell, .dataCell {
        width: 10rem;
    }
    .dateLabelColumn,
    .dataLabelColumn,
    .dateCell,
    .dataCell {
        flex-shrink: 0;
        flex-grow: 0;
    }
    #dateRow {
        top: 0;
        z-index: 11;
    }
    .fit {
        flex-shrink: 0;
        min-width: max-content;
    }
    .modalFieldLabel {
        width: 6rem;
        flex-shrink: 0;
    }
    .textInput > :first-child > :first-child {
        font-size: small;
    }
</style>

<template>
    <div v-if="pageData.data !== undefined" id="summaryVitals" class="bg-white">
        <div id="header" class="d-flex justify-content-between mt-3 mb-3">
            <div id="chartName" class="fw-bold">{{ `${pageData.patientInfo?.patientName} ${pageData.patientInfo?.patientAge} ${pageData.patientInfo?.genderDescription}` }}</div>
            <ChoiceButtonGroup v-if="pageData.selectedTab === TabType.table" v-model:selectedValue="pageData.dateRange" :items=dateRangeOptions :button-size="ButtonDisplaySize.Smaller"/>
        </div>
        <div id="body" class="d-flex me-2">
            <div id="tabContainer" class="w-25">
                <TabContainer v-model="pageData.selectedTab">
                    <TabItem title="Graph" :value="TabType.graph" class="tab">
                        <iframe :src="graphLaunchUrl" style="height: 90vh; width: 100%;"></iframe>
                    </TabItem>
                    <TabItem title="Table" :value="TabType.table" class="tab">
                        <div v-if="pageData.resultsPanelOpen || pageData.vitalsPanelOpen" id="dateRow" class="d-flex pt-4 pb-3 fit bg-white sticky">
                            <div class="dateLabelColumn sticky bg-white"></div>
                            <div
                                v-for="(item, index) in filteredData" 
                                :key="index"
                                class="dateCell d-flex justify-content-center"
                            >
                                <div>{{ item.date }}</div>
                                <Icon
                                    v-if="item.fbResults?.find(result => result.hasOrder)"
                                    :icon="IconTypes.Flask"
                                    title="Open order"
                                    class="ms-2 pointer text-success"
                                    @click="openOrder(item.date)"
                                />
                            </div>
                        </div>
                        <div ref="resultsPanel" class="mt-1">
                            <FoldingPanel v-model:panelOpen="pageData.resultsPanelOpen" @click="updatePanelExpandedSetting(true)" class="fit">
                                <template #title>
                                    Results
                                </template>
                                <template #body>
                                    <div class="fit">
                                        <!-- fishbone results -->
                                        <div
                                            v-for="(fbHeader, i) in pageData.fishboneHeaders"
                                            :key="i"
                                        >
                                            <div v-if="fishboneResultExists(fbHeader.fbId)">
                                                <div
                                                    class="dataLabelColumn d-flex sticky text-light bg-secondary border-end"
                                                >
                                                    <div class="p-2">{{ fbHeader.fbDescription }}</div>
                                                    <Icon 
                                                        :icon="IconTypes.New"
                                                        :title="`Create ${fbHeader.fbDescription} result`"
                                                        class="click text-white"
                                                        style="margin-top: 4%"
                                                        @click="initializeNewResultWithResultType(fbHeader.fbId)"
                                                    />
                                                </div>
                                                <div
                                                    v-for="(fbType, j) in pageData.fishboneTypes.filter(fbType => fbType.fbId === fbHeader.fbId && fishboneResultItemExists(fbType.fbSubId))" 
                                                    :key="j"
                                                    class="d-flex border-bottom"
                                                    :class="j % 2 === 0 ? 'bg-light' : 'bg-white'"
                                                >
                                                    <div 
                                                        class="dataLabelColumn sticky text-dark p-2 border-end"
                                                        :class="j % 2 === 0 ? 'bg-light' : 'bg-white'"
                                                    >
                                                        {{ fbType.fbSubLabel }}
                                                    </div>
                                                    <div
                                                        v-for="(dataItem, k) in filteredData"
                                                        :key="k"
                                                        class="d-flex"
                                                    >
                                                        <div 
                                                            class="dataCell p-2 border-end d-flex justify-content-center"
                                                            :class="[
                                                                { 'pointer' : dataItem.fbResults?.filter(item => item.fbSubId == fbType.fbSubId).length },
                                                                getFishboneResultValueTextColor(fbType.fbSubId, dataItem.fbResults)
                                                            ]"
                                                            :title="getFishboneResultTitle(fbType.fbSubId, dataItem.fbResults)"
                                                            @click="() => { editState.fishboneResult = getFishboneResult(fbType.fbSubId, dataItem.fbResults) }"
                                                        >
                                                            {{ getFishboneResultValue(fbType.fbSubId, dataItem.fbResults) ?? '-' }}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- custom results -->
                                        <div
                                            v-for="(customResultHeader, i) in pageData.customResultHeaders"
                                            :key="i"
                                        >
                                            <div
                                                class="dataLabelColumn sticky text-light bg-secondary border-end"
                                            >
                                                <div class="p-2">{{ `Custom #${i+1}` }}</div>
                                            </div>
                                            <div class="d-flex bg-light text-dark border-bottom">
                                                <div
                                                    class="dataLabelColumn sticky bg-light p-2 border-end"
                                                >
                                                    {{ customResultHeader.description }}
                                                </div>
                                                <div
                                                    v-for="(dataItem, k) in filteredData"
                                                    :key="k"
                                                    class="d-flex dataCell p-2 border-end d-flex justify-content-center"
                                                    :class="{ 'pointer' : dataItem.customResults?.length }"
                                                    @click="() => { editState.customResult = dataItem.customResults?.[0] }"
                                                >
                                                    {{ dataItem.customResults?.[0]?.globalEntityId == customResultHeader.globalEntityId ? dataItem.customResults?.[0]?.encounterValue ?? '-' : '-' }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </FoldingPanel>
                        </div>
                        <div ref="vitalsPanel">
                            <FoldingPanel v-model:panelOpen="pageData.vitalsPanelOpen" @click="updatePanelExpandedSetting(false)" class="d-flex fit">
                                <template #title>
                                    Vitals
                                </template>
                                <template #body>
                                    <div>
                                        <div class="d-flex bg-light border-bottom">
                                            <div class="dataLabelColumn sticky bg-light p-2 border-end">BP</div>
                                            <div
                                                v-for="(item, index) in filteredData" 
                                                :key="index"
                                                class="dataCell p-2 border-end d-flex justify-content-center"
                                                :class="{ 'pointer' : item.vitals?.length }"
                                                @click="() => { editState.vital = item.vitals?.[0] }"
                                            >
                                                <span class="me-1" :class="[getSystolicTextColor(item.vitals?.[0]?.systolic)]">
                                                    {{ `${item.vitals?.[0]?.systolic ?? '-'} ` }}
                                                </span>
                                                {{ `/` }}
                                                <span class="mx-1" :class="[getDiastolicTextColor(item.vitals?.[0]?.diastolic)]">
                                                    {{ ` ${item.vitals?.[0]?.diastolic ?? '-'}` }}
                                                </span>
                                                {{ `${item.vitals?.[0]?.systolic != null || item.vitals?.[0]?.diastolic != null ? 'mmHg' : ''}` }}
                                            </div>
                                        </div>
                                        <div class="d-flex border-bottom">
                                            <div class="dataLabelColumn sticky bg-white p-2 border-end">Pulse</div>
                                            <div
                                                v-for="(item, index) in filteredData" 
                                                :key="index"
                                                class="dataCell p-2 border-end d-flex justify-content-center"
                                                :class="{ 'pointer' : item.vitals?.length }"
                                                @click="() => { editState.vital = item.vitals?.[0] }"
                                            >
                                                {{ item.vitals?.[0]?.pulse ? `${item.vitals?.[0]?.pulse} bpm` : '-' }}
                                            </div>
                                        </div>
                                        <div class="d-flex bg-light border-bottom">
                                            <div class="dataLabelColumn sticky bg-light p-2 border-end">Temperature</div>
                                            <div
                                                v-for="(item, index) in filteredData" 
                                                :key="index"
                                                class="dataCell p-2 border-end d-flex justify-content-center"
                                                :class="{ 'pointer' : item.vitals?.length }"
                                                @click="() => { editState.vital = item.vitals?.[0] }"
                                            >
                                                {{ item.vitals?.[0]?.temperature ? `${item.vitals?.[0]?.temperature}&deg${item.vitals?.[0]?.measurementSystem == MeasurementSystem.Imperial ? 'F' : 'C'}` : '-' }}
                                            </div>
                                        </div>
                                        <div class="d-flex border-bottom">
                                            <div class="dataLabelColumn sticky bg-white p-2 border-end">Weight</div>
                                            <div
                                                v-for="(item, index) in filteredData" 
                                                :key="index"
                                                class="dataCell p-2 border-end d-flex justify-content-center"
                                                :class="{ 'pointer' : item.vitals?.length }"
                                                @click="() => { editState.vital = item.vitals?.[0] }"
                                            >
                                                {{ item.vitals?.[0]?.weight ? item.vitals?.[0]?.weight : '-' }}
                                            </div>
                                        </div>
                                        <div class="d-flex bg-light border-bottom">
                                            <div class="dataLabelColumn sticky bg-light p-2 border-end">Height</div>
                                            <div
                                                v-for="(item, index) in filteredData" 
                                                :key="index"
                                                class="dataCell p-2 border-end d-flex justify-content-center"
                                                :class="{ 'pointer' : item.vitals?.length }"
                                                @click="() => { editState.vital = item.vitals?.[0] }"
                                            >
                                                {{ item.vitals?.[0]?.height ? item.vitals?.[0]?.height : '-' }}
                                            </div>
                                        </div>
                                        <div class="d-flex border-bottom">
                                            <div class="dataLabelColumn sticky bg-white p-2 border-end">BMI</div>
                                            <div
                                                v-for="(item, index) in filteredData" 
                                                :key="index"
                                                class="dataCell p-2 border-end d-flex justify-content-center"
                                                :class="[{ 'pointer' : item.vitals?.length }, getBmiTextColor(item.vitals?.[0]?.bmi)]"
                                                @click="() => { editState.vital = item.vitals?.[0] }"
                                            >
                                                {{ item.vitals?.[0]?.bmi ? item.vitals?.[0]?.bmi : '-' }}
                                            </div>
                                        </div>
                                        <div class="d-flex bg-light border-bottom">
                                            <div class="dataLabelColumn sticky bg-light p-2 border-end">Respiration</div>
                                            <div
                                                v-for="(item, index) in filteredData" 
                                                :key="index"
                                                class="dataCell p-2 border-end d-flex justify-content-center"
                                                :class="{ 'pointer' : item.vitals?.length }"
                                                @click="() => { editState.vital = item.vitals?.[0] }"
                                            >
                                                {{ item.vitals?.[0]?.respirations ? `${item.vitals?.[0]?.respirations} bpm` : '-' }}
                                            </div>
                                        </div>
                                        <div class="d-flex border-bottom">
                                            <div class="dataLabelColumn sticky bg-white p-2 border-end">Pain</div>
                                            <div
                                                v-for="(item, index) in filteredData" 
                                                :key="index"
                                                class="dataCell p-2 border-end d-flex justify-content-center"
                                                :class="{ 'pointer' : item.vitals?.length }"
                                                @click="() => { editState.vital = item.vitals?.[0] }"
                                            >
                                                {{ item.vitals?.[0]?.pain ?? '-' }}
                                            </div>
                                        </div>
                                        <div class="d-flex bg-light">
                                            <div class="dataLabelColumn sticky bg-light p-2 border-end">SpO2</div>
                                            <div
                                                v-for="(item, index) in filteredData" 
                                                :key="index"
                                                class="dataCell p-2 border-end d-flex justify-content-center"
                                                :class="{ 'pointer' : item.vitals?.length }"
                                                @click="() => { editState.vital = item.vitals?.[0] }"
                                            >
                                                {{ item.vitals?.[0]?.spO2 ? `${item.vitals?.[0]?.spO2}%` : '-' }}
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </FoldingPanel>
                        </div>
                    </TabItem>
                </TabContainer>
            </div>
            <div v-if="pageData.selectedTab == TabType.table" class="d-flex w-25 justify-content-center">
                <Icon 
                    :icon="IconTypes.New"
                    title="Create new result"
                    class="mt-3 pe-3 click"
                    @click="initializeNewResult"
                />
                <Icon 
                    :icon="IconTypes.Refresh"
                    title="Refresh table"
                    class="mt-3 ps-3 click border-start"
                    @click="initialize"
                />
            </div>
            <ModalDialog 
                :show="editState.vital != undefined"
                :size="ModalSize.Medium"
                @hide="() => { editState.vital = undefined }"
            >
                <template #title><div class="modalHeader">{{ `Edit Vital - ${editState.vital?.encounterDate}` }}</div></template>
                <div class="d-flex ms-2 mb-2">
                    <div class="modalFieldLabel mt-2 me-3">{{'BP'}}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.systolic"
                            :disabled='editState.vital.systolic === null'
                        />
                    </div>
                    <div class="mt-2 ms-3 me-3">{{'/'}}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.diastolic"
                            :disabled='editState.vital.diastolic === null'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ 'mmHg' }}</div>
                </div>
                <div class="d-flex ms-2 mb-2">
                    <div class="modalFieldLabel mt-2 me-3">{{'Pulse'}}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.pulse"
                            :disabled='editState.vital.pulse === null'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ 'bpm' }}</div>
                </div>
                <div class="d-flex ms-2 mb-2">
                    <div class="modalFieldLabel mt-2 me-3">{{'Temperature'}}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.temperature"
                            :disabled='editState.vital.temperature === null'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ `&deg;${editState.vital?.measurementSystem == MeasurementSystem.Imperial ? 'F' : 'C'}` }}</div>
                </div>
                <div class="d-flex ms-2 mb-2">
                    <div class="modalFieldLabel mt-2 me-3">{{'Weight'}}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.weightPrimaryValue"
                            :disabled='!editState.vital.weight'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ `${editState.vital?.measurementSystem == MeasurementSystem.Imperial ? 'lbs' : 'Kgs'}` }}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.weightSecondaryValue"
                            :disabled='!editState.vital.weight'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ `${editState.vital?.measurementSystem == MeasurementSystem.Imperial ? 'ozs' : 'g'}` }}</div>
                </div>
                <div class="d-flex ms-2 mb-2">
                    <div class="modalFieldLabel mt-2 me-3">{{'Height'}}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.heightPrimaryValue"
                            :disabled='!editState.vital.height'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ `${editState.vital?.measurementSystem == MeasurementSystem.Imperial ? 'ft' : 'M'}` }}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.heightSecondaryValue"
                            :disabled='!editState.vital.height'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ `${editState.vital?.measurementSystem == MeasurementSystem.Imperial ? 'in' : 'cm'}` }}</div>
                </div>
                <div class="d-flex ms-2 mb-2">
                    <div class="modalFieldLabel mt-2 me-3">{{'Respirations'}}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.respirations"
                            :disabled='editState.vital.respirations === null'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ 'bpm' }}</div>
                </div>
                <div class="d-flex ms-2 mb-2">
                    <div class="modalFieldLabel mt-2 me-3">{{'Pain'}}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.pain"
                            :disabled='editState.vital.pain === null'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ '(1-10)' }}</div>
                </div>
                <div class="d-flex ms-2 mb-2">
                    <div class="modalFieldLabel mt-2 me-3">{{'SpO2'}}</div>
                    <div class="textInput" style="width: 15%">
                        <TextInput
                            v-if="editState.vital"
                            v-model:textValue="editState.vital.spO2"
                            :disabled='editState.vital.spO2 === null'
                        />
                    </div>
                    <div class="mt-2 ms-2 me-3">{{ '%' }}</div>
                </div>
                <template #footer>
                    <ActionButtonV2
                        :action="ActionButtonStyle.Save"
                        :display-style="ButtonDisplayStyle.OutlinePrimary"
                        :performAction="updateVital"
                        text="Save"
                    />
                </template>
            </ModalDialog>

            <!-- Fishbone Result edit form -->
            <ModalDialog 
                :show="editState.fishboneResult != undefined"
                :size="ModalSize.Medium"
                @hide="() => { editState.fishboneResult = undefined }"
            >
                <template #title><div class="modalHeader">{{ `Edit ${editState.fishboneResult?.fbDescription} Result - ${editState.fishboneResult?.labReportDate}`}}</div></template>
                <div class="d-flex ms-2 mb-2">
                    <div class="mt-2 me-3">{{editState.fishboneResult?.fbResultLabel}}</div>
                    <div class="textInput" style="width: 25%">
                        <TextInput
                            v-if="editState.fishboneResult"
                            v-model:textValue="editState.fishboneResult.resultValue"
                            :disabled='editState.fishboneResult.isTemplateResult === false'
                        />
                    </div>
                    <div class="mt-2 ms-3 me-3">{{editState.fishboneResult?.unitOfMeasure}}</div>
                </div>
                <template #footer>
                    <ActionButtonV2
                        :action="ActionButtonStyle.Save"
                        :display-style="ButtonDisplayStyle.OutlinePrimary"
                        :performAction="updateFishoneResult"
                        :disabled='editState.fishboneResult?.isTemplateResult === false'
                        text="Save"
                    />
                </template>
            </ModalDialog>

            <!-- Custom Result edit form -->
            <ModalDialog 
                :show="editState.customResult != undefined"
                :size="ModalSize.Medium"
                @hide="() => { editState.customResult = undefined }"
            >
                <template #title><div class="modalHeader">{{ `Edit Custom Result - ${editState.customResult?.encounterDate}` }}</div></template>
                <div class="d-flex ms-2 mb-2">
                    <div class="mt-2 me-3">{{editState.customResult?.description}}</div>
                    <div class="textInput" style="width: 25%">
                        <TextInput
                            v-if="editState.customResult"
                            v-model:textValue="editState.customResult.encounterValue"
                        />
                    </div>
                    <div class="mt-2 ms-3 me-3">{{editState.customResult?.units}}</div>
                </div>
                <template #footer>
                    <ActionButtonV2
                        :action="ActionButtonStyle.Save"
                        :display-style="ButtonDisplayStyle.OutlinePrimary"
                        :performAction="updateCustomResult"
                        text="Save"
                    />
                </template>
            </ModalDialog>

            <!-- New result entry form -->
            <ModalDialog 
                :show="editState.newResult != undefined"
                :size="ModalSize.Large"
                @hide="() => { editState.newResult = undefined }"
            >
                <template #title><div class="modalHeader">{{ `Lab Result Entry - ${pageData.patientInfo?.patientName}` }}</div></template>
                <div v-if="editState.newResult" class="ms-2 mb-2">
                    <div class="d-flex mt-2 me-3">
                        <div class="fw-bold mt-1 me-3">{{`Date`}}</div>
                        <div style="width: 25%">
                            <DatePickerNoTZ 
                                v-model:value="editState.newResult.date"
                                :clearable="false"
                                :required="true"
                                :display-format="DateDisplayFormat.NumericDate"
                            />
                        </div>
                    </div>
                    <div class="d-flex mt-4 me-3">
                        <div class="fw-bold mt-1 me-3">{{`Result Type`}}</div>
                        <div style="width: 25%">
                            <v-select
                                v-model="editState.newResult.resultType"
                                :options="resultTypes"
                                :reduce="(item: ITimelineResultType) => item.id"
                                label="displayName"
                            />
                        </div>
                    </div>
                    <div v-if="editState.newResult.resultType" class="d-flex mt-4">
                        <div class="fw-bold" style="width: 24%; margin-right: 1%">{{`Result Name`}}</div>
                        <div class="fw-bold" style="width: 24%; margin-right: 1%">{{`Template`}}</div>
                        <div class="fw-bold" style="width: 24%; margin-right: 1%">{{`Result Value`}}</div>
                        <div class="fw-bold" style="width: 24%">{{`Abnormal`}}</div>
                    </div>
                    <div
                        v-for="(item, i) in pageData.fishboneTypes.filter(fbType => fbType.fbId == editState.newResult?.resultType && fbType.templates?.length)" 
                        class="mt-3 me-3"
                    >
                        <div class="d-flex">
                            <div style="width: 24%; margin-right: 1%">{{item.fbSubLabel}}</div>
                            <div style="width: 24%; margin-right: 1%">
                                <v-select
                                    v-if="editState.newResult.resultItems.length"
                                    v-model="editState.newResult.resultItems[i].template"
                                    :options="item.templates"
                                    label="templateName"
                                />
                            </div>
                            <div style="width: 24%; margin-right: 1%" class="textInput">
                                <TextInput
                                    v-if="editState.newResult.resultItems.length"
                                    v-model:textValue="editState.newResult.resultItems[i].value"
                                />
                            </div>
                            <div style="width: 24%; margin-right: 1%">
                                <v-select
                                    v-if="editState.newResult.resultItems.length"
                                    v-model="editState.newResult.resultItems[i].abnormal"
                                    :options="ResultAbnormalOptions"
                                    :reduce="(item: ITimelineResultAbnormal) => item.value"
                                    label="label"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <template #footer>
                    <ActionButtonV2
                        :action="ActionButtonStyle.Save"
                        :display-style="ButtonDisplayStyle.OutlinePrimary"
                        :performAction="createNewResult"
                        text="Save"
                    />
                </template>
            </ModalDialog>
        </div>
    </div>
</template>

